import { Grid } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import {
  CardComponent,
  NoDataVisual,
  RatingCard,
  StarRating,
  TextField,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import React, { useEffect, useState } from 'react';
import RatingReviewTabMessages from '../../../core-utils/messages/CareNavigatorConstants';
import Loader from '../../molecules/Loader';
import { formatRatingCardData } from '../../../core-utils/helper-utils/careNavigatorHelper';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import {
  getRatingColor,
  getRatingText,
} from '../../../core-utils/helper-utils/helper';
import {
  SimpleSearch,
  Typography,
} from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import properties from '../../../core-utils/properties';
import axios from 'axios';
import { Pagination } from '@mui/material';
import { AlertColor as MuiColor } from '@mui/material/Alert';
import Snackbar from '../../atoms/Snackbar';
import { useStores } from '../../../stores';

export interface RatingReviewTabProps {}

const RatingReviewTab: React.FC<RatingReviewTabProps> = ({}) => {
  const useStyles = makeStyles()({
    careNavCards: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      padding: '20px 0px',
      gap: '20px',
    },
    container: {
      display: 'flex',
      flexDirection: 'column',
      padding: '20px 20px',
    },
    totalRating: {
      border: '1px solid #E5E9F0',
      padding: '20px',
      display: 'flex',
      flexDirection: 'column',
    },
    allRatings: {
      display: 'flex',
      flexDirection: 'row',
      gap: '20px',
      marginTop: '20px',
    },
    emptyData: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'center',
      padding: '20px',
    },
    pagination: {
      display: 'flex',
      justifyContent: 'right',
      marginTop: '16px',
    },
  });

  const messages =
    RatingReviewTabMessages.CARE_NAVIGATOR_PAGE.RATING_REVIEW_DATA;

  const careNavigatorStore = useStores().careNavigatorStore;

  const [loader] = useState<any>(<Loader />);
  const [loading, setLoading] = useState<boolean>(false);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [ratings, setRatings] = useState<any>({
    avgRating: 0,
    data: [],
    ratingData: {
      '1': 0,
      '2': 0,
      '3': 0,
      '4': 0,
      '5': 0,
    },
    totalCount: 0,
  });
  const [snackbarState, setSnackbarState] = React.useState<{
    open: boolean;
    message: string;
    severity: MuiColor | undefined;
    duration: null | number;
  }>({
    open: false,
    message: '',
    severity: undefined,
    duration: null,
  });

  const handleCloseSnackBar = () => {
    setSnackbarState({
      open: false,
      message: '',
      severity: undefined,
      duration: null,
    });
  };

  const handleOpenSnackBar = (
    message: string,
    severity: MuiColor,
    duration: number | null,
  ) => {
    setSnackbarState({
      open: true,
      message: message,
      severity: severity,
      duration: duration,
    });
  };

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number,
  ) => {
    setPageNumber(value);
  };

  const { classes } = useStyles();

  const getRatings = async (value = '') => {
    return await axios.post(
      properties.aiqBaseUrl +
        properties.caseSurveyBaseUrl +
        properties.getCaseSurveys,
      {
        pageNumber: pageNumber - 1,
        pageSize: 6,
        searchValue: value,
        groupIds: [careNavigatorStore.group_id],
      },
    );
  };

  const apiCalls = async () => {
    setLoading(true);
    await Promise.all([getRatings()])
      .then((responses: any) => {
        responses.map((res: any) => {
          if (res?.status !== 200) {
            handleOpenSnackBar(res?.reason?.message, 'error', 6000);
          }
        });

        if (responses[0]?.status === 200) {
          setRatings(responses[0]?.data?.data);
          setTotalPages((responses[0]?.data?.data?.totalCount ?? 0) / 6);
        }
      })
      .then(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    apiCalls();
  }, [pageNumber]);

  const handleSearch = async (value: string) => {
    if (value === '') {
      await Promise.all([getRatings('')]).then((responses: any) => {
        responses.map((res: any) => {
          if (res?.status !== 200) {
            handleOpenSnackBar(res?.reason?.message, 'error', 6000);
          }
        });

        if (responses[0]?.status === 200) {
          setRatings(responses[0]?.data?.data);
          setTotalPages((responses[0]?.data?.data?.totalCount ?? 0) / 6);
        }
      });
    }

    if (!value || value.length <= 2) {
      return;
    }

    await Promise.all([getRatings(value)]).then((responses: any) => {
      responses.map((res: any) => {
        if (res?.status !== 200) {
          handleOpenSnackBar(res?.reason?.message, 'error', 6000);
        }
      });

      if (responses[0]?.status === 200) {
        setRatings(responses[0]?.data?.data);
        setTotalPages((responses[0]?.data?.data?.totalCount ?? 0) / 6);
      }
    });
  };

  return (
    <Grid>
      {loading ? (
        loader
      ) : (
        <Grid>
          <CardComponent
            title={messages.title}
            subTitle={messages.subTitle}
            width="1176px"
            mainGridStyles={{ padding: '20px' }}
            body={
              <Grid className={classes.container}>
                <Grid className={classes.totalRating}>
                  <Typography
                    fontSize="16px"
                    lineHeight="25px"
                    fontType="medium"
                    textColor={getRatingColor(ratings.avgRating)}
                  >
                    {getRatingText(ratings.avgRating)}
                  </Typography>
                  <StarRating
                    value={ratings.avgRating}
                    color={getRatingColor(ratings.avgRating)}
                    size="large"
                    precision={0.1}
                    showRating={
                      <Grid
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          gap: '10px',
                          alignItems: 'center',
                        }}
                      >
                        <Typography
                          fontSize="22px"
                          lineHeight="35px"
                          fontType="medium"
                          textColor={
                            patientAccessTheme.palette.text.highemphasis
                          }
                        >
                          {Number(ratings.avgRating).toFixed(1)}
                        </Typography>
                        <Typography
                          fontSize="12px"
                          lineHeight="19px"
                          fontType="normal"
                          textColor={
                            patientAccessTheme.palette.text.lowemphasis
                          }
                        >
                          (Overall rating)
                        </Typography>
                      </Grid>
                    }
                    readOnly={true}
                  />
                  <Grid className={classes.allRatings}>
                    {Object.entries(ratings.ratingData).map(([key, value]) => (
                      <StarRating
                        value={Number(key)}
                        color={getRatingColor(Number(key))}
                        size="small"
                        precision={0.1}
                        showRating={
                          <Grid
                            style={{
                              display: 'flex',
                              flexDirection: 'row',
                              gap: '10px',
                              alignItems: 'center',
                            }}
                          >
                            <Typography
                              fontSize="12px"
                              lineHeight="19px"
                              fontType="normal"
                              textColor={
                                patientAccessTheme.palette.text.highemphasis
                              }
                            >
                              ({String(value)})
                            </Typography>
                            <Typography
                              fontSize="12px"
                              lineHeight="19px"
                              fontType="bold"
                              textColor={getRatingColor(Number(key))}
                            >
                              {getRatingText(Number(key))}
                            </Typography>
                          </Grid>
                        }
                        readOnly={true}
                      />
                    ))}
                  </Grid>
                </Grid>
                {ratings?.data?.length === 0 ? (
                  <Grid className={classes.emptyData}>
                    <NoDataVisual content="No Data Available" height="200px" />
                  </Grid>
                ) : (
                  <Grid>
                    <Grid className={classes.careNavCards}>
                      {ratings.data?.map((item: any) => (
                        <RatingCard
                          {...formatRatingCardData(item)}
                          height="210px"
                          width="538px"
                        />
                      ))}
                    </Grid>
                    <div className={classes.pagination}>
                      <Pagination
                        page={pageNumber}
                        count={totalPages}
                        onChange={handlePageChange}
                        size="small"
                        color="primary"
                      />
                    </div>
                  </Grid>
                )}
              </Grid>
            }
            topRightItem={
              <TextField
                placeholder={'Search'}
                height={'36px'}
                width={'305px'}
                startAdornment={
                  <SimpleSearch
                    height="20px"
                    width="20px"
                    stroke={patientAccessTheme.palette.text.lowemphasis}
                  />
                }
                onChange={(event: any) => handleSearch(event.target.value)}
                showStartAdornment={true}
                style={{ marginRight: '30px' }}
              />
            }
          />
        </Grid>
      )}
      <Snackbar
        {...snackbarState}
        vertical={'bottom'}
        horizontal={'right'}
        handleClose={handleCloseSnackBar}
      />
    </Grid>
  );
};
export default RatingReviewTab;
